/**
 * sourceDImage — les octets de l'image qu'`insert_image` pose ou qu'`edit_image`
 * substitue (#2141), quelle que soit leur provenance.
 *
 * Trois provenances, une seule forme rendue :
 *
 * - une pièce du dossier ou un fichier joint à la conversation : le BACKEND a
 *   appliqué les gardes d'accès, contrôlé le type et le poids, et joint les
 *   octets à l'enveloppe de l'action (`imageBytes`). Le navigateur ne va rien
 *   chercher : sans octets joints, il refuse.
 * - une image déjà dans le corps de l'acte : ses octets sont dans le fichier de
 *   l'acte. Le navigateur l'exporte en mémoire et y lit le média que l'image
 *   pointe.
 *
 * ## Mesuré sur superdoc@2.15.1 (2026-10-12, `mesure-images-du-corps.spec.ts`, M6 à M9)
 *
 * - `images.list` rend, pour chaque image, `properties.src` : le chemin de son
 *   média dans l'archive (`word/media/signature.png`).
 * - `create.image` et `images.replaceSource` n'acceptent qu'une data URI base64.
 *   PNG et JPEG sont écrits tels quels dans un média neuf (`word/media/imageN.png`
 *   ou `.jpeg`), octets identiques à la source.
 * - L'export en mémoire d'un acte de 10 Ko prend 6 ms ; 0,4 à 1,2 s sur un acte
 *   de 1,5 Mo (`exportDeLActe.ts`).
 *
 * Non mesuré : une image dont le média est partagé avec un en-tête, un média
 * lié et non embarqué (`TargetMode="External"`), un JPEG dont l'orientation
 * EXIF tourne l'image.
 */

import JSZip from 'jszip';
import {
  CANVAS_IMAGE_SOURCE_MAX_BYTES,
  tailleEnPixelsDeLImage,
  typeDImageDesOctets,
  type CanvasImageBytes,
  type CanvasImageMimeType,
  type CanvasImageSource,
} from '@hector/shared-types';
import type { CanvasActionReceipt } from './actionReceipt';
import { ExportDeLActeImpossible, exporterLActeEnMemoire } from './exportDeLActe';
import { mediasDesImages, type ImageDuCorps, type ImageListee } from './imagesDuCorps';
import type { CanvasActionContext } from './textActionExecutors';

type Taille = { width: number; height: number };

/** Une image prête à être remise à l'éditeur. */
export interface ImageAPoser {
  mimeType: CanvasImageMimeType;
  /** La seule forme que l'éditeur accepte comme `src`. */
  dataUri: string;
  /**
   * La taille dont l'image garde les proportions : celle inscrite dans ses
   * octets, ou, pour la copie d'une image de l'acte, la taille AFFICHÉE de
   * l'originale — une signature réduite à 269 px se copie à 269 px, pas aux
   * 1 200 px de son scan.
   */
  taille: Taille;
  /** D'où elle vient, dit pour le reçu : « attachment tmp-42 », « image img:… ». */
  origine: string;
}

export type SourceDImageResolue =
  | { ok: true; image: ImageAPoser }
  | { ok: false; code: string; message: string; recovery: NonNullable<CanvasActionReceipt['recovery']> };

function octetsDuBase64(base64: string): Uint8Array {
  const binaire = atob(base64);
  const octets = new Uint8Array(binaire.length);
  for (let rang = 0; rang < binaire.length; rang += 1) octets[rang] = binaire.charCodeAt(rang);
  return octets;
}

function base64DesOctets(octets: Uint8Array): string {
  // Par tranches : `String.fromCharCode(...octets)` dépasse la pile d'appels sur une image de deux mégaoctets.
  let binaire = '';
  for (let rang = 0; rang < octets.length; rang += 0x8000) binaire += String.fromCharCode(...octets.subarray(rang, rang + 0x8000));
  return btoa(binaire);
}

/** Le chemin du média d'une image dans l'archive, sans barre initiale : `images.list` le publie avec ou sans. */
async function mediaDeLImage(doc: unknown, imageId: string): Promise<string | undefined> {
  return (await mediasDesImages(doc)).get(imageId)?.replace(/^\//, '');
}

function refus(code: string, message: string, action: 'stop' | 'retry' | 'reinspect', reason: string): SourceDImageResolue {
  return { ok: false, code, message, recovery: { action, reason } };
}

/** Les octets joints par le backend, relus comme il les a contrôlés : le navigateur ne pose pas ce qu'il n'a pas reconnu. */
function imageJointe(verbe: string, source: CanvasImageSource, jointe: CanvasImageBytes | undefined): SourceDImageResolue {
  const designation = source.documentId !== undefined ? `case document ${source.documentId}` : `attachment ${source.attachmentId}`;
  if (jointe === undefined) {
    return refus('IMAGE_BYTES_MISSING',
      `${verbe} refused before any write: the request names ${designation} as the image source but carries no image bytes. The server reads the file and sends it with the action; here it sent none. Nothing was written. Do not resend this call: tell the lawyer the image could not be delivered to the editor and ask them to reload the page.`,
      'stop', 'The image bytes did not travel with the action; resending the same call would fail the same way.');
  }
  const octets = octetsDuBase64(jointe.base64);
  const type = typeDImageDesOctets(octets);
  const taille = type.posable ? tailleEnPixelsDeLImage(octets, type.mimeType) : undefined;
  if (!type.posable || taille === undefined) {
    return refus('IMAGE_SOURCE_UNREADABLE',
      `${verbe} refused before any write: the file of ${designation} is not a readable PNG or JPEG image (${type.posable ? 'its pixel size could not be read' : `its content is ${type.nom}`}). Nothing was written. Ask the lawyer for the image as a PNG or JPEG file.`,
      'stop', 'The source file is not an image the editor can place.');
  }
  return { ok: true, image: { mimeType: type.mimeType, dataUri: `data:${type.mimeType};base64,${jointe.base64}`, taille, origine: designation } };
}

/** L'image du corps à copier : son média lu dans le fichier de l'acte exporté en mémoire. */
async function imageCopiee(
  ctx: CanvasActionContext,
  verbe: string,
  imageId: string,
  lu: { toutes: readonly ImageListee[]; duCorps: readonly ImageDuCorps[]; nommees: string },
): Promise<SourceDImageResolue> {
  const originale = lu.duCorps.find((image) => image.imageId === imageId);
  if (originale === undefined) {
    return lu.toutes.some((image) => image.imageId === imageId)
      ? refus('IMAGE_NOT_IN_BODY',
        `${verbe} refused before any write: source image ${imageId} belongs to a header or a footer, and only a body image can be copied. ${lu.nommees} Nothing was written.`,
        'stop', 'Header and footer images cannot be used as a source.')
      : refus('IMAGE_NOT_FOUND',
        `${verbe} refused before any write: no image has the id ${imageId} given as source.imageId. ${lu.nommees} Resend the action with one of these ids, exactly as written.`,
        'retry', 'Refused before any write: resend with a source image id from the message, without re-reading.');
  }
  const media = await mediaDeLImage(ctx.doc, imageId);
  if (media === undefined) {
    return refus('IMAGE_SOURCE_UNREADABLE',
      `${verbe} refused before any write: the editor does not say which file of the document holds image ${imageId}, so it cannot be copied. Nothing was written. Ask the lawyer for the image as a PNG or JPEG file and insert it from that file.`,
      'stop', 'The editor publishes no media path for this image.');
  }
  let octets: Uint8Array | undefined;
  try {
    const archive = await JSZip.loadAsync(await exporterLActeEnMemoire(ctx.doc));
    octets = await archive.file(media)?.async('uint8array');
  } catch (erreur) {
    const cause = erreur instanceof ExportDeLActeImpossible ? erreur.message : `the exported file could not be opened (${erreur instanceof Error ? erreur.message : String(erreur)}).`;
    return refus('IMAGE_SOURCE_UNREADABLE',
      `${verbe} refused before any write: image ${imageId} could not be read from the document file: ${cause} Resend the same call once; if it fails again, ask the lawyer for the image as a PNG or JPEG file.`,
      'retry', 'Refused before any write: the document file could not be read; one more try is safe.');
  }
  // L'export est une attente : l'avocat a pu ouvrir un autre document.
  ctx.verifierEpoque(`${verbe}:source-lue`);
  if (octets === undefined) {
    return refus('IMAGE_SOURCE_UNREADABLE',
      `${verbe} refused before any write: image ${imageId} points to ${media}, which the document file does not contain (the picture may be linked, not embedded). Nothing was written. Ask the lawyer for the image as a PNG or JPEG file.`,
      'stop', 'The media file of the source image is not in the document.');
  }
  const type = typeDImageDesOctets(octets);
  if (!type.posable) {
    return refus('IMAGE_SOURCE_TYPE_UNSUPPORTED',
      `${verbe} refused before any write: image ${imageId} is stored as ${type.nom} (${media}), and only a PNG or JPEG image can be copied. Nothing was written. To move it, ask the lawyer to do it in Word, or ask them for the image as a PNG or JPEG file.`,
      'stop', 'The source image is not a PNG or a JPEG.');
  }
  if (octets.length > CANVAS_IMAGE_SOURCE_MAX_BYTES) {
    return refus('IMAGE_SOURCE_TOO_LARGE',
      `${verbe} refused before any write: image ${imageId} weighs ${(octets.length / (1024 * 1024)).toFixed(1)} MB (${media}), over the ${CANVAS_IMAGE_SOURCE_MAX_BYTES / (1024 * 1024)} MB an inserted image may weigh. Nothing was written. Tell the lawyer this image is too heavy to be copied from here.`,
      'stop', 'The source image is over the size limit.');
  }
  // La taille AFFICHÉE de l'originale quand elle se lit ; à défaut, celle des octets.
  const taille = originale.width > 0 && originale.height > 0 ? { width: originale.width, height: originale.height } : tailleEnPixelsDeLImage(octets, type.mimeType);
  if (taille === undefined) {
    return refus('IMAGE_SOURCE_UNREADABLE',
      `${verbe} refused before any write: the size of image ${imageId} could not be read, neither in the editor nor in its file (${media}). Nothing was written. Ask the lawyer for the image as a PNG or JPEG file.`,
      'stop', 'The source image has no readable size.');
  }
  return { ok: true, image: { mimeType: type.mimeType, dataUri: `data:${type.mimeType};base64,${base64DesOctets(octets)}`, taille, origine: `image ${imageId}` } };
}

/**
 * Résout la source d'une image, sans rien écrire.
 *
 * @param lu Les images du document, déjà lues par le verbe ; `nommees` est la
 *   phrase qui nomme celles du corps, reprise telle quelle dans un refus.
 */
export function resoudreLaSourceDImage(
  ctx: CanvasActionContext,
  verbe: string,
  source: CanvasImageSource,
  lu: { toutes: readonly ImageListee[]; duCorps: readonly ImageDuCorps[]; nommees: string },
): Promise<SourceDImageResolue> | SourceDImageResolue {
  return source.imageId !== undefined ? imageCopiee(ctx, verbe, source.imageId, lu) : imageJointe(verbe, source, ctx.imageJointe);
}
